(() => {
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function isVisible(element) {
    if (!element) return false;
    return Boolean(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
  }

  function initMobileMenuAccessibility(scope = document) {
    const toggle = scope.querySelector?.('[data-menu-toggle]') || document.querySelector('[data-menu-toggle]');
    const menu = scope.querySelector?.('[data-mobile-menu]') || document.querySelector('[data-mobile-menu]');
    if (!toggle || !menu || menu.dataset.a11yReady === 'true') return;
    menu.dataset.a11yReady = 'true';

    if (!menu.id) menu.id = 'MobilePrimaryNavigation';
    toggle.setAttribute('aria-controls', menu.id);

    const sync = () => {
      const open = menu.classList.contains('open');
      toggle.setAttribute('aria-expanded', String(open));
      toggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
      menu.setAttribute('aria-hidden', String(!open));
      if (open) menu.removeAttribute('inert');
      else menu.setAttribute('inert', '');
    };

    sync();
    new MutationObserver(sync).observe(menu, { attributes: true, attributeFilter: ['class'] });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !menu.classList.contains('open')) return;
      menu.classList.remove('open');
      sync();
      toggle.focus();
    });
  }

  function activeModalDialog() {
    const nativeDialog = [...document.querySelectorAll('dialog[open]')].find(isVisible);
    if (nativeDialog) return nativeDialog;

    const candidates = [
      ...document.querySelectorAll('.online-game-modal:not([hidden]) [role="dialog"][aria-modal="true"]'),
      ...document.querySelectorAll('.arcade-drawer:not([hidden]) [role="dialog"][aria-modal="true"]')
    ];
    return candidates.find(isVisible) || null;
  }

  function trapModalFocus(event) {
    if (event.key !== 'Tab') return;
    const modal = activeModalDialog();
    if (!modal) return;

    const focusable = [...modal.querySelectorAll(focusableSelector)].filter(isVisible);
    if (!focusable.length) {
      event.preventDefault();
      modal.setAttribute('tabindex', '-1');
      modal.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || !modal.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !modal.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  }

  function enhanceExternalLinks(scope = document) {
    scope.querySelectorAll?.('a[target="_blank"]').forEach((link) => {
      if (link.dataset.a11yExternalReady === 'true') return;
      link.dataset.a11yExternalReady = 'true';
      if (!link.getAttribute('aria-label')) {
        const label = (link.textContent || '').replace(/↗/g, '').trim();
        if (label) link.setAttribute('aria-label', `${label} (opens in a new tab)`);
      }
    });
  }

  function enhanceDynamicStatus(scope = document) {
    const polite = [
      '[data-member-alias]',
      '[data-member-reward-status]',
      '[data-lb-summary]'
    ];
    polite.forEach((selector) => {
      scope.querySelectorAll?.(selector).forEach((element) => {
        if (!element.hasAttribute('aria-live')) element.setAttribute('aria-live', 'polite');
      });
    });
  }

  function focusFormFeedback(scope = document) {
    const error = scope.querySelector?.('[data-form-errors], .form .errors');
    const success = scope.querySelector?.('[data-form-status]');
    const target = error || success;
    if (!target) return;
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    requestAnimationFrame(() => target.focus({ preventScroll: false }));
  }

  function markCurrentPage(scope = document) {
    const current = new URL(window.location.href);
    scope.querySelectorAll?.('.site-header a[href], .site-footer a[href]').forEach((link) => {
      try {
        const url = new URL(link.href, window.location.href);
        if (url.origin !== current.origin || url.hash || current.hash) return;
        if (url.pathname === current.pathname && url.search === current.search) {
          link.setAttribute('aria-current', 'page');
        }
      } catch (error) {}
    });
  }

  function init(scope = document) {
    initMobileMenuAccessibility(scope);
    enhanceExternalLinks(scope);
    enhanceDynamicStatus(scope);
    focusFormFeedback(scope);
    markCurrentPage(scope);
  }

  document.addEventListener('keydown', trapModalFocus);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
